import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import cn from 'clsx';
import sharedStyles from '../styles.module.scss';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import ChipList from '@components-clean/core/chip-list';
import { datasetFilterValues } from '@components-clean/core/comparison-dataset-card/comparison-dataset-card.data';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import { Popup } from '@components-clean/core/popup/popup';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import Tooltip from '@components-clean/core/tooltip';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { IDatasetFilters, StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { useLocalState, useTranslation } from '@hooks/index';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

interface IProps {
    onApply?: () => void;
    filteredDatasets: CM2Dataset[];
}

const DatasetSelectionDropdown = ({ onApply, filteredDatasets }: IProps) => {
    const { t } = useTranslation(['cm', 'ra']);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const { current, setCurrent, setComparisonDataset } = useContext(StudySiteHubContext);
    const { dispatch } = useLocalState();

    const {
        control,
        reset,
        handleSubmit,
        formState: { isValid },
    } = useForm<IDatasetFilters>({
        mode: 'all',
        defaultValues: current.datasetFilters,
    });

    const checkWholeNumber = (dataSetsCount: number | null | undefined) => {
        // dataSetsCount check will return false for a value of 0 so had to add that check first
        return dataSetsCount === 0 || (dataSetsCount && (dataSetsCount * 10) % 10 === 0);
    };

    const checkDataSetCountValidity = (dataSetsCount: number) => {
        if (dataSetsCount != null && checkWholeNumber(dataSetsCount)) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    };

    const datasetSelectorOnApply = (data: IDatasetFilters) => {
        if (checkWholeNumber(data.dataSetsCount)) {
            setCurrent({
                ...current,
                datasetFilters: data,
            });
            setComparisonDataset(data.comparisonDataset ?? undefined);
            dispatch({
                type: 'SET_SELECTED_COMPARISON_CM2_DATASET',
                selectedComparisonCM2Dataset: data.comparisonDataset ?? null,
            });
            onApply && onApply();
        } else {
            setIsDisabled(true);
        }
    };

    return (
        <div className={styles.datasetSelector} data-testid="dataset-selector">
            <div className={styles.datasetSelectorContents}>
                <div>
                    {t('comparisonDataset')}:
                    <div className="k-h-fit">
                        <DropDownListController
                            control={control}
                            name="comparisonDataset"
                            inline
                            data-testid="comparison-dataset-selector"
                            data={filteredDatasets}
                            placeholder={t('comparisonDataset')}
                            dataItemKey="id"
                            textField="dataCutDate"
                            value={
                                filteredDatasets.find((d) => d.id === current.datasetFilters.comparisonDataset?.id) ??
                                null
                            }
                            handleInputChange={() => {}}
                            type="card-header"
                        />
                    </div>
                </div>

                <div>
                    <span>{t('studyHubPage.datasetFilter')}:</span>
                    <Controller
                        control={control}
                        name="dataSetFilter"
                        render={({ field: { onChange, value } }) => (
                            <ChipList
                                data-testid="dataset-filter-chip-list"
                                value={String(value)} // Kendo needs value as string
                                onChange={(e) => onChange(Number(e.value))} // Convert to number for BFF
                                defaultData={datasetFilterValues}
                            />
                        )}
                    />
                </div>

                <div>
                    <span>{t('studyHubPage.displayLast')}:</span>
                    <div className="k-display-flex k-align-items-center k-gap-5 k-mr-2">
                        <NumericTextBoxController
                            control={control}
                            name="dataSetsCount"
                            required
                            data-testid="datasets-to-display-numeric-textbox"
                            format="#"
                            min={0}
                            max={12}
                            style={{ width: 155 }}
                            handleInputChange={(event) => {
                                const { value } = event.target;
                                if (value === 0 || value) {
                                    checkDataSetCountValidity(Number(value));
                                }
                            }}
                        />
                        <span>{t('studyHubPage.datasets')}</span>
                    </div>
                </div>
            </div>
            <hr style={{ margin: '18px 0 6px 0' }} />
            <div className="k-display-flex k-justify-content-end k-gap-3">
                <Button
                    onClick={() => {
                        reset(current.defaultDatasetFilters);
                    }}
                    data-testid="reset-button"
                >
                    {t('studyHubPage.reset')}
                </Button>
                <Button
                    primary
                    onClick={handleSubmit(datasetSelectorOnApply)}
                    data-testid="apply-button"
                    disabled={isDisabled || !isValid}
                >
                    {t('studyHubPage.apply')}
                </Button>
            </div>
        </div>
    );
};

const DatasetSelectionButton = () => {
    const { current, setComparisonDataset } = useContext(StudySiteHubContext);
    const [showPopup, setShowPopup] = useState(false);
    const { dispatch } = useLocalState();
    const closePopup = () => {
        setShowPopup(false);
    };
    const { data: datasets } = DatasetApi.useGetActive(current.studyRef);

    const sortedDatasets = (datasets ?? []).map(formatDatasetDates).sort(sortDatasetByDatesDesc);

    const filteredDatasets = useMemo(() => {
        return sortedDatasets.filter(
            (d) =>
                d.id !== current.dataset.id &&
                d.dataCutDate &&
                new Date(d.dataCutDate) < new Date(current.dataset.date),
        );
    }, [sortedDatasets]);

    const anchor = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        dispatch({
            type: 'SET_SELECTED_COMPARISON_CM2_DATASET',
            selectedComparisonCM2Dataset: null,
        });
    }, []);

    useEffect(() => {
        if (
            current.datasetFilters.comparisonDataset &&
            new Date(current.dataset.date) <= new Date(current.datasetFilters.comparisonDataset.dataCutDate!)
        ) {
            setComparisonDataset(undefined);
        }
    }, [current.dataset]);

    return (
        <div className={cn('k-d-flex', styles.datasetSelectorButton)}>
            <div ref={anchor}>
                <Button
                    data-testid="dataset-selection-btn"
                    alternativeStyle
                    primary
                    faIcon={['far', 'database']}
                    onClick={() => {
                        setShowPopup(!showPopup);
                    }}
                    className={sharedStyles.actionBarButtons}
                    size="small"
                    faIconStyle={{ width: 12, height: 13 }}
                />
                <Popup
                    data-testid="dataset-selector-popup"
                    anchor={anchor.current}
                    border="noBorder"
                    show={showPopup}
                    onBlur={closePopup}
                    style={{
                        marginTop: '6px',
                    }}
                >
                    <DatasetSelectionDropdown onApply={closePopup} filteredDatasets={filteredDatasets} />
                </Popup>
            </div>
            {current.datasetFilters.comparisonDataset?.dataCutDate && (
                <ul className={cn('k-d-flex k-gap-1 k-flex-auto', styles.comparisonDateChipList)}>
                    <Tooltip position="top" anchorElement="target" parentTitle>
                        <button
                            type="button"
                            className={sharedStyles.chip}
                            onClick={() => {
                                setShowPopup(!showPopup);
                            }}
                        >
                            <span>{current.datasetFilters.comparisonDataset.dataCutDate}</span>
                        </button>
                    </Tooltip>
                </ul>
            )}
        </div>
    );
};

export default DatasetSelectionButton;
